import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Building2, Plus, Star, Globe, MapPin, Search, Loader2, ExternalLink,
  Sparkles, BadgeCheck, SlidersHorizontal, ArrowUpDown,
} from "lucide-react";

const QUICK_SEARCHES = [
  "Domaine Saint-Émilion",
  "Château Margaux",
  "Vignerons Bourgogne",
  "Domaine Bio Loire",
  "Cave coopérative Provence",
  "Champagne Reims",
];

const SORT_OPTIONS = [
  { key: "relevance", label: "Pertinence" },
  { key: "rating", label: "Note ⭐ (déc.)" },
  { key: "rating_count", label: "Nb d'avis (déc.)" },
  { key: "name", label: "Nom (A→Z)" },
];

export default function Producers() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mine, setMine] = useState(null);

  // Filters / sort
  const [minRating, setMinRating] = useState(0);
  const [onlyClaimed, setOnlyClaimed] = useState(false);
  const [withWebsite, setWithWebsite] = useState(false);
  const [sort, setSort] = useState("relevance");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (user) api.get("/producers-mine/me").then((r) => setMine(r.data)).catch(() => setMine(null));
  }, [user]);

  const runSearch = async (text) => {
    const cleaned = (text || "").trim();
    if (!cleaned || cleaned.length < 2) {
      setResults([]);
      setSubmittedQ("");
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    setSubmittedQ(cleaned);
    try {
      const { data } = await api.get("/producers/places/search", { params: { q: cleaned, limit: 20 } });
      setResults(Array.isArray(data?.results) ? data.results : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Recherche impossible. Réessayez.");
      setResults([]);
    } finally { setLoading(false); }
  };

  const onSubmit = (e) => { e.preventDefault(); runSearch(q); };
  const onPreset = (preset) => { setQ(preset); runSearch(preset); };

  const filteredSorted = useMemo(() => {
    let arr = results.slice();
    if (minRating > 0) arr = arr.filter((r) => (r.rating || 0) >= minRating);
    if (onlyClaimed) arr = arr.filter((r) => !!r.geovinum_slug);
    if (withWebsite) arr = arr.filter((r) => !!r.website);
    if (sort === "rating") arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (sort === "rating_count") arr.sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
    else if (sort === "name") arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return arr;
  }, [results, minRating, onlyClaimed, withWebsite, sort]);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="producers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <span className="chip-pink inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Recherche live · Google Maps</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Producteurs</h1>
          <p className="text-[#4A5568] mt-3">
            Tapez le nom d'un domaine, une appellation, une région — geoVINUM AI ramène les fiches Google Maps en direct,
            avec notes, photos, contacts et accès aux fiches geoVINUM enrichies.
          </p>
        </div>
        {user && (
          <Link
            to={mine ? "/producers/mine" : "/producers/new"}
            data-testid="producers-cta"
            className="btn-primary"
          >
            <Plus className="w-4 h-4" /> {mine ? "Modifier ma fiche" : "Créer ma fiche gratuite"}
          </Link>
        )}
      </div>

      {/* Search */}
      <form onSubmit={onSubmit} className="mt-8 max-w-2xl">
        <div className="flex items-center gap-2 bg-white border border-[#1F3A5F]/15 focus-within:border-[#E5276A] rounded-xl px-4 py-3 shadow-sm">
          <Search className="w-5 h-5 text-[#E5276A] shrink-0" />
          <input
            data-testid="producers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ex : Domaine Saint-Émilion, Château Margaux, Vignerons Bourgogne…"
            className="flex-1 bg-transparent outline-none text-base text-[#1F3A5F] placeholder:text-[#8B95A1]"
            autoFocus
          />
          <button
            type="submit"
            data-testid="producers-search-submit"
            disabled={loading || !q.trim() || q.trim().length < 2}
            className="px-4 py-1.5 rounded-lg bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Chercher"}
          </button>
        </div>

        {/* Quick searches */}
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A1] font-bold mr-1 py-1">Exemples :</span>
          {QUICK_SEARCHES.map((p) => (
            <button
              key={p} type="button" onClick={() => onPreset(p)}
              data-testid={`producers-preset-${p.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-2.5 py-1 rounded-full text-[11px] font-bold text-[#1F3A5F] bg-white border border-[#1F3A5F]/12 hover:border-[#E5276A] hover:text-[#E5276A] transition-colors"
            >
              {p}
            </button>
          ))}
        </div>
      </form>

      {/* Filters bar (visible once we have results) */}
      {submittedQ && results.length > 0 && (
        <div className="mt-6 flex items-center flex-wrap gap-3 text-sm" data-testid="producers-filters">
          <button
            type="button" onClick={() => setShowFilters((s) => !s)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A] text-xs font-bold"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> Filtres
          </button>
          <div className="inline-flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-[#8B95A1]" />
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              data-testid="producers-sort"
              className="bg-white border border-[#1F3A5F]/15 rounded-lg px-2 py-1.5 text-xs text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]">
              {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <span className="text-xs text-[#8B95A1] ml-auto">
            {filteredSorted.length} résultat{filteredSorted.length > 1 ? "s" : ""} pour « <b className="text-[#1F3A5F]">{submittedQ}</b> »
          </span>
        </div>
      )}

      {showFilters && submittedQ && (
        <div className="mt-3 rounded-xl bg-white border border-[#1F3A5F]/12 p-4 grid sm:grid-cols-3 gap-3 text-xs" data-testid="producers-filters-panel">
          <label className="flex items-center justify-between gap-2">
            <span className="text-[#1F3A5F] font-bold">Note min</span>
            <select value={minRating} onChange={(e) => setMinRating(parseFloat(e.target.value))}
              className="bg-white border border-[#1F3A5F]/15 rounded px-2 py-1">
              <option value="0">Toutes</option>
              <option value="3">3 ★</option>
              <option value="4">4 ★</option>
              <option value="4.5">4.5 ★</option>
            </select>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={onlyClaimed} onChange={(e) => setOnlyClaimed(e.target.checked)}
              className="w-4 h-4 accent-[#E5276A]" />
            <span className="text-[#1F3A5F]">Fiche geoVINUM uniquement</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={withWebsite} onChange={(e) => setWithWebsite(e.target.checked)}
              className="w-4 h-4 accent-[#E5276A]" />
            <span className="text-[#1F3A5F]">Avec site web</span>
          </label>
        </div>
      )}

      {/* Results */}
      <div className="mt-8" data-testid="producers-results">
        {!submittedQ && !loading && (
          <div className="rounded-3xl border-2 border-dashed border-[#E5276A]/25 p-10 text-center text-[#8B95A1]" data-testid="producers-empty-state">
            <Building2 className="w-10 h-10 mx-auto text-[#E5276A]/40 mb-3" />
            <p className="text-[#1F3A5F] font-bold">Commencez par une recherche</p>
            <p className="text-sm mt-1">« Domaine Saint-Émilion » · « Château Margaux » · « Vignerons Bourgogne »</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-12 text-[#8B95A1] inline-flex items-center gap-2 w-full justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Recherche en cours…
          </div>
        )}

        {error && !loading && (
          <p className="text-center py-10 text-[#E5276A]">{error}</p>
        )}

        {!loading && submittedQ && filteredSorted.length === 0 && !error && (
          <p className="text-center py-12 text-[#8B95A1]">Aucun résultat. Essayez avec d'autres mots-clés.</p>
        )}

        {!loading && filteredSorted.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="producers-grid">
            {filteredSorted.map((p) => <ProducerCard key={p.place_id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ProducerCard({ p }) {
  const photo = p.photos?.[0] || p.photo_url;
  return (
    <div className="app-card overflow-hidden group" data-testid={`producer-card-${p.place_id}`}>
      <div className="aspect-[4/3] relative bg-gradient-to-br from-[#FBF7F3] via-[#FCE4ED]/30 to-[#FBF7F3] overflow-hidden">
        {photo ? (
          <img src={photo} alt={p.name} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <Building2 className="w-12 h-12 text-[#E5276A]/30 absolute inset-0 m-auto" />
        )}
        {p.geovinum_slug && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1F3A5F] text-white text-[10px] font-bold uppercase tracking-wider">
            <BadgeCheck className="w-3 h-3" /> geoVINUM
          </div>
        )}
        {p.open_now != null && (
          <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${p.open_now ? "bg-emerald-500 text-white" : "bg-white text-[#8B95A1]"}`}>
            {p.open_now ? "Ouvert" : "Fermé"}
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-display text-lg text-[#1F3A5F] leading-tight group-hover:text-[#E5276A] transition-colors line-clamp-2">
          {p.name}
        </h3>
        {p.address && <p className="text-xs text-[#8B95A1] mt-1 line-clamp-2">{p.address}</p>}

        <div className="mt-3 flex items-center gap-3 text-xs">
          {p.rating != null && (
            <span className="inline-flex items-center gap-1 text-[#1F3A5F]">
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
              <b>{p.rating}</b>
              {p.rating_count != null && <span className="text-[#8B95A1]">({p.rating_count})</span>}
            </span>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {p.geovinum_slug && (
            <Link to={`/producers/${p.geovinum_slug}`} data-testid={`producer-card-${p.place_id}-go`}
              className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#E5276A] text-white">
              Fiche complète
            </Link>
          )}
          {p.gmaps_url && (
            <a href={p.gmaps_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#1F3A5F] hover:text-[#E5276A]">
              <MapPin className="w-3 h-3" /> Maps <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {p.website && (
            <a href={p.website} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#1F3A5F] hover:text-[#E5276A]">
              <Globe className="w-3 h-3" /> Site
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
